import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import { api } from "@/lib/api";
import { betaReportStrings } from "@/lib/betaReport/i18n";
import { useBetaReportWording } from "@/lib/betaReport/wording";

export const STRINGS = {
  "pt-BR": {
    step: "Antes de começar",
    title: "Escolha o formato da sua prática",
    langLabel: "Idioma da conta",
    langHint: "Todo o conteúdo do aplicativo será exibido neste idioma. As perguntas da entrevista permanecem em inglês.",
    quickTitle: "Prática rápida",
    quickCount: "6 perguntas",
    quickDesc: "Ideal para uma revisão rápida ou quando você tem pouco tempo.",
    advancedTitle: "Prática avançada",
    advancedCount: "10 perguntas",
    advancedDesc: "Cobertura mais ampla para uma preparação profunda antes de uma entrevista real.",
    modeTitle: "Como você quer praticar?",
    guidedTitle: "Modo Guiado",
    guidedDesc: "Você vê cada pergunta e controla o ritmo: grave quando estiver pronto e envie quando terminar.",
    liveTitle: "Modo Avançado",
    liveDesc: "Como uma entrevista real: só áudio, sem texto. Você tem 30 segundos para começar a responder, e o entrevistador continua quando você para de falar.",
    sameNote: "Ambos os formatos usam a mesma metodologia, competências e critérios de avaliação. Muda apenas a quantidade de perguntas.",
    continue: "Continuar para envio de currículo",
    picker: "Selecione um formato para continuar",
    limitReached: (limit = 10, until = "") => `Você já usou as ${limit} sessões de entrevista do seu acesso de 30 dias.${until ? ` Seu acesso termina em ${until}.` : ""} Seus relatórios continuam disponíveis.`,
    freeTitle: "Teste Gratuito Limitado",
    freeDesc: "Sua Conta Gratuita inclui uma única entrevista de 6 perguntas para você experimentar o Interview Simulator. Ao concluir, você receberá o feedback completo da Pergunta 1.",
    freeNoReport: "O relatório completo, os downloads, a repetição e o reinício exigem uma conta paga.",
    freeUsedTitle: "Seu Teste Gratuito Limitado já foi utilizado",
    freeUsedBody: "A Conta Gratuita inclui um único Teste Gratuito Limitado de 6 perguntas. O acesso completo de 30 dias ficará disponível como opção de compra.",
    tier: "Tipo de acesso",
  },
  en: {
    step: "Before you begin",
    title: "Choose your practice format",
    langLabel: "Account language",
    langHint: "All app content will appear in this language. Interview questions are in English.",
    quickTitle: "Quick Practice",
    quickCount: "6 questions",
    quickDesc: "Great for a fast tune-up or when you're short on time.",
    advancedTitle: "Advanced Practice",
    advancedCount: "10 questions",
    advancedDesc: "Broader coverage for deep prep before a real interview.",
    modeTitle: "How do you want to practise?",
    guidedTitle: "Guided Mode",
    guidedDesc: "You see each question and set the pace: record when you’re ready and submit when you’re done.",
    liveTitle: "Advanced Mode",
    liveDesc: "Like a real interview: audio only, no text. You have 30 seconds to start answering, and the interviewer moves on when you stop speaking.",
    sameNote: "Both formats use the same methodology, competencies, and scoring — only the number of questions changes.",
    continue: "Continue to résumé upload",
    picker: "Select a format to continue",
    limitReached: (limit = 10, until = "") => `You’ve used all ${limit} interview sessions in your 30-day access.${until ? ` Your access ends on ${until}.` : ""} Your reports are still available.`,
    freeTitle: "Free Limited Trial",
    freeDesc: "Your Free Account includes one 6-question interview so you can experience the Interview Simulator. When you finish, you’ll receive the complete feedback for Question 1.",
    freeNoReport: "The full report, downloads, re-practice, and restart require a paid account.",
    freeUsedTitle: "Your Free Limited Trial has already been used",
    freeUsedBody: "A Free Account includes one 6-question Free Limited Trial. Full 30-day access will be available as a purchase option.",
    tier: "Access type",
  },
  "es-419": {
    step: "Antes de comenzar",
    title: "Elija el formato de su práctica",
    langLabel: "Idioma de la cuenta",
    langHint: "Todo el contenido de la aplicación aparecerá en este idioma. Las preguntas de la entrevista permanecen en inglés.",
    quickTitle: "Práctica rápida",
    quickCount: "6 preguntas",
    quickDesc: "Ideal para una revisión rápida o cuando tiene poco tiempo.",
    advancedTitle: "Práctica avanzada",
    advancedCount: "10 preguntas",
    advancedDesc: "Cobertura más amplia para una preparación profunda antes de una entrevista real.",
    modeTitle: "¿Cómo quiere practicar?",
    guidedTitle: "Modo Guiado",
    guidedDesc: "Ve cada pregunta y marca el ritmo: grabe cuando esté listo y envíe cuando termine.",
    liveTitle: "Modo Avanzado",
    liveDesc: "Como una entrevista real: solo audio, sin texto. Tiene 30 segundos para empezar a responder, y el entrevistador continúa cuando deja de hablar.",
    sameNote: "Ambos los formatos usan la misma metodología, competencias y evaluación — solo cambia la cantidad de preguntas.",
    continue: "Continuar al envío del currículum",
    picker: "Seleccione un formato para continuar",
    limitReached: (limit = 10, until = "") => `Ya utilizó las ${limit} sesiones de entrevista de su acceso de 30 días.${until ? ` Su acceso termina el ${until}.` : ""} Sus informes siguen disponibles.`,
    freeTitle: "Prueba Gratuita Limitada",
    freeDesc: "Su Cuenta Gratuita incluye una entrevista única de 6 preguntas para probar el Interview Simulator. Al finalizar, recibirá los comentarios completos de la Pregunta 1.",
    freeNoReport: "El informe completo, las descargas, la repetición y el reinicio requieren una cuenta de pago.",
    freeUsedTitle: "Su Prueba Gratuita Limitada ya fue utilizada",
    freeUsedBody: "La Cuenta Gratuita incluye una sola Prueba Gratuita Limitada de 6 preguntas. El acceso completo de 30 días estará disponible como opción de compra.",
    tier: "Tipo de acceso",
  },
  "fr-CA": {
    step: "Avant de commencer",
    title: "Choisissez votre format de pratique",
    langLabel: "Langue du compte",
    langHint: "Tout le contenu de l’application s’affichera dans cette langue. Les questions d’entrevue restent en anglais.",
    quickTitle: "Pratique rapide",
    quickCount: "6 questions",
    quickDesc: "Idéal pour une mise au point rapide ou quand vous avez peu de temps.",
    advancedTitle: "Pratique avancée",
    advancedCount: "10 questions",
    advancedDesc: "Couverture plus large pour une préparation approfondie avant une vraie entrevue.",
    modeTitle: "Comment voulez-vous pratiquer?",
    guidedTitle: "Mode guidé",
    guidedDesc: "Vous voyez chaque question et choisissez le rythme : enregistrez quand vous êtes prêt et envoyez quand vous avez terminé.",
    liveTitle: "Mode avancé",
    liveDesc: "Comme une vraie entrevue : audio seulement, sans texte. Vous avez 30 secondes pour commencer à répondre, et l’intervieweur continue quand vous arrêtez de parler.",
    sameNote: "Les deux formats utilisent la même méthodologie, compétences et évaluation — seul le nombre de questions change.",
    continue: "Continuer vers le téléversement du CV",
    picker: "Sélectionnez un format pour continuer",
    limitReached: (limit = 10, until = "") => `Vous avez utilisé les ${limit} sessions d’entrevue de votre accès de 30 jours.${until ? ` Votre accès se termine le ${until}.` : ""} Vos rapports restent disponibles.`,
    freeTitle: "Essai Gratuit Limité",
    freeDesc: "Votre compte gratuit comprend une seule entrevue de 6 questions pour essayer l’Interview Simulator. À la fin, vous recevrez la rétroaction complète pour la Question 1.",
    freeNoReport: "Le rapport complet, les téléchargements, la nouvelle pratique et le redémarrage nécessitent un compte payant.",
    freeUsedTitle: "Votre Essai Gratuit Limité a déjà été utilisé",
    freeUsedBody: "Un compte gratuit comprend un seul Essai Gratuit Limité de 6 questions. L’accès complet de 30 jours sera offert comme option d’achat.",
    tier: "Type d’accès",
  },
};

const LANG_DISPLAY = {
  "pt-BR": "Português (Brasil)",
  en: "English",
  "es-419": "Español (América Latina)",
  "fr-CA": "Français (Canada)",
};

function pickStrings(lang) {
  return STRINGS[lang] || STRINGS.en;
}

export default function PracticeFormat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useBetaReportWording(); // published Beta Report wording (2026-11-04)
  const [choice, setChoice] = useState("");
  const [allowance, setAllowance] = useState(null);
  const [entitlement, setEntitlement] = useState(null);

  const lang = user?.preferred_language || "en";
  const t = pickStrings(lang);
  const isAdmin = Boolean(user?.is_admin);
  const tier = isAdmin ? "admin" : entitlement?.access_tier;
  const isFree = tier === "free";
  const freeUsed = isFree && Boolean(entitlement?.free_trial_used);

  const loadState = async () => {
    try {
      const [ent, allow] = await Promise.all([
        api.get("/access-entitlement"),
        api.get("/session-allowance"),
      ]);
      setEntitlement(ent.data || null);
      setAllowance(allow.data || null);
      return { entitlement: ent.data || null, allowance: allow.data || null };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const refresh = async () => {
      try {
        const [ent, allow] = await Promise.all([
          api.get("/access-entitlement"),
          api.get("/session-allowance"),
        ]);
        if (!alive) return;
        setEntitlement(ent.data || null);
        setAllowance(allow.data || null);
      } catch {
        // Backend remains the authoritative gate.
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [user?.user_id]);

  // Live Interview mode (LIVE_INTERVIEW_PLAN.md §8, 2026-10-30). Guided is the default (L5);
  // the free trial stays Guided.
  const [mode, setMode] = useState("guided");
  // A beta tester submits the Beta Report for the last interview first (2026-10-30).
  const [pendingReport, setPendingReport] = useState(null);
  useEffect(() => {
    if (tier !== "beta") return;
    api.get("/beta-report/pending").then(({ data }) => setPendingReport(data?.session_id || null)).catch(() => {});
  }, [tier]);

  useEffect(() => {
    if (isFree && !freeUsed) setChoice("quick");
    if (freeUsed) setChoice("");
  }, [isFree, freeUsed]);

  const limitDisabled = Boolean(allowance?.limit_disabled);
  const atLimit = tier === "paid" && !limitDisabled && Boolean(allowance) && Number(allowance.used) >= Number(allowance.limit);
  const statePending = !entitlement || !allowance;

  useEffect(() => {
    if (atLimit) setChoice("");
  }, [atLimit]);

  // The 6-question format is retired except for the free trial (decided 2026-10-30):
  // every other account practises with 10 questions, Guided or Live Interview.
  useEffect(() => {
    if (!isFree && !freeUsed && !atLimit) setChoice("advanced");
  }, [isFree, freeUsed, atLimit]);

  const onContinue = async () => {
    if (!choice || atLimit || freeUsed || statePending) return;
    const latest = await loadState();
    if (!latest) return;
    const latestTier = latest.entitlement?.access_tier;
    if (latestTier === "free") {
      if (latest.entitlement?.free_trial_used) {
        setChoice("");
        return;
      }
      navigate("/upload", { state: { practice_format: "quick", mode: "guided" } });
      return;
    }
    if (latestTier === "paid" && !latest.allowance?.limit_disabled && Number(latest.allowance?.used) >= Number(latest.allowance?.limit)) {
      setChoice("");
      return;
    }
    const chosenMode = isFree ? "guided" : mode;
    try { sessionStorage.setItem("mec_interview_mode", chosenMode); } catch { /* storage may be blocked */ }
    navigate("/upload", { state: { practice_format: choice, mode: chosenMode } });
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="practice-format-screen">
      <header className="border-b border-[color:var(--border-stone)] bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/globe-logo.svg" alt="English Job Interview Simulator" className="h-[63px] w-[86px] object-contain" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)] leading-tight">
              English Job Interview Simulator
            </div>
          </div>
          <div className="flex items-center gap-3"><UserMenu /></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 md:p-10 space-y-8">
        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-4 flex items-start justify-between gap-4" data-testid="practice-format-language-row">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{t.langLabel}</div>
            <div className="mt-1 text-sm font-medium text-[color:var(--text-primary)]" data-testid="practice-format-language-value">{LANG_DISPLAY[lang] || lang}</div>
            <p className="mt-1 text-[11px] text-[color:var(--text-secondary)] max-w-md">{t.langHint}</p>
          </div>
          <div className="text-right">
            <span className="inline-block rounded-full border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{lang}</span>
            {entitlement?.tier_label && <div className="mt-2 text-[10px] text-[color:var(--text-secondary)]">{t.tier}: <span className="font-medium text-[color:var(--text-primary)]">{entitlement.tier_label}</span></div>}
          </div>
        </section>

        {pendingReport && (
          <section className="rounded-2xl border border-[color:var(--brand)]/40 bg-[color:var(--brand)]/5 p-4" role="alert" data-testid="beta-report-pending">
            <p className="text-sm font-medium text-[color:var(--text-primary)] leading-relaxed">{betaReportStrings(lang).ui.pending}</p>
            <button type="button" onClick={() => navigate(`/beta-report/${pendingReport}`)} className="mt-3 rounded-full bg-[color:var(--brand)] text-white px-5 py-2 text-sm font-medium">{betaReportStrings(lang).ui.pendingOpen}</button>
          </section>
        )}

        {atLimit && (
          <section className="rounded-2xl border border-[color:var(--score-bad)]/40 bg-[color:var(--score-bad)]/5 p-4" data-testid="daily-session-limit-banner" role="alert">
            <p className="text-sm font-medium text-[color:var(--text-primary)] leading-relaxed">{t.limitReached(allowance?.limit || 10, allowance?.period_end ? new Date(allowance.period_end).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : "")}</p>
          </section>
        )}

        {freeUsed && (
          <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5" data-testid="free-diagnostic-used" role="status">
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t.freeUsedTitle}</h2>
            <p className="mt-2 text-sm text-[color:var(--text-secondary)] leading-relaxed">{t.freeUsedBody}</p>
          </section>
        )}

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand)]">{t.step}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--text-primary)]" data-testid="practice-format-title">{isFree ? t.freeTitle : t.title}</h1>
          {isFree && !freeUsed && <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">{t.freeDesc}</p>}
        </div>

        {!freeUsed && (
          <div className="grid gap-4 grid-cols-1">
            {isFree && <FormatCard id="quick" selected={choice === "quick"} onSelect={() => setChoice("quick")} title={t.freeTitle} count={t.quickCount} description={t.freeDesc} testId="format-quick" disabled={atLimit || statePending} />}
            {!isFree && <FormatCard id="advanced" selected={choice === "advanced"} onSelect={() => setChoice("advanced")} title={t.advancedTitle} count={t.advancedCount} description={t.advancedDesc} testId="format-advanced" disabled={atLimit || statePending} />}
          </div>
        )}

        {!freeUsed && !isFree && (
          <div className="space-y-3" data-testid="practice-mode-choice">
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t.modeTitle}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <FormatCard id="guided" name="interview_mode" selected={mode === "guided"} onSelect={() => setMode("guided")} title={t.guidedTitle} description={t.guidedDesc} testId="mode-guided" disabled={atLimit || statePending} />
              <FormatCard id="live" name="interview_mode" selected={mode === "live"} onSelect={() => setMode("live")} title={t.liveTitle} description={t.liveDesc} testId="mode-live" disabled={atLimit || statePending} />
            </div>
          </div>
        )}

        {!freeUsed && isFree && <p className="text-[11px] text-[color:var(--text-secondary)] leading-relaxed" data-testid="practice-format-methodology-note">{t.freeNoReport}</p>}

        {!freeUsed && (
          <div className="pt-2">
            <button type="button" onClick={onContinue} disabled={!choice || atLimit || statePending || Boolean(pendingReport)} className="w-full sm:w-auto rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-3 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="practice-format-continue">
              {choice ? t.continue : t.picker}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function FormatCard({ id, selected, onSelect, title, count, description, testId, disabled = false, name = "practice_format" }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      data-testid={testId}
      className={`text-left rounded-2xl border p-5 transition-all ${disabled ? "border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] opacity-45 cursor-not-allowed" : selected ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5 ring-2 ring-[color:var(--brand)]/40 shadow-md" : "border-[color:var(--border-stone)] bg-white hover:border-[color:var(--brand)]/40 hover:bg-[color:var(--bg-surface-hover)]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          {count && <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{count}</div>}
          <div className="mt-1 text-xl font-semibold tracking-tight text-[color:var(--text-primary)]">{title}</div>
        </div>
        <span data-testid={`${testId}-radio`} className={`mt-1 inline-block h-5 w-5 rounded-full border-2 transition ${selected ? "border-[color:var(--brand)] bg-[color:var(--brand)]" : "border-[color:var(--border-stone)] bg-white"}`} aria-hidden="true">
          {selected && <span className="block h-full w-full rounded-full ring-2 ring-white" />}
        </span>
      </div>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">{description}</p>
      <input type="radio" name={name} value={id} checked={selected} onChange={onSelect} disabled={disabled} className="sr-only" data-testid={`${testId}-input`} />
    </button>
  );
}
